import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { DirectDebitForm } from '../../components/finance/DirectDebitForm';
import { toast } from 'sonner';
import { DirectDebit, DirectDebitSettings } from '../../types/direct-debit';
import { financeApi } from '../../utils/finance-api';
import { api } from '../../utils/api';
import { RefreshCw, ChevronLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import AccessRestricted from '../../components/AccessRestricted';

const DirectDebitEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const ability = useAbility(AbilityContext);
  const canUpdate = ability.can(ACTIONS.UPDATE, RESOURCES.DIRECT_DEBIT);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [debit, setDebit] = useState<DirectDebit | null>(null);
  const [settings, setSettings] = useState<DirectDebitSettings | null>(null);

  useEffect(() => {
    if (canUpdate) loadData();
  }, [id, canUpdate]);

  if (!canUpdate) {
    return (
      <AccessRestricted
        navigateTo="/finance/direct-debit"
        backMessage="Back to Direct Debit Register"
        accessMessage="You don't have permission to edit direct debit records."
      />
    );
  }

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [debitRes, companiesRes, departmentsRes] = await Promise.all([
        financeApi.getDirectDebit(id!),
        financeApi.listCompanies(),
        // Picker data comes from the selection endpoint: finance roles do not
        // hold read:department_list, so /api/departments answers 403 and used to
        // abort this whole load (error toast + no form even though the record
        // itself loaded). Same endpoint the other module pickers use.
        api.get<{ departments: any[] }>('/api/departments/selection')
      ]);
      setDebit(debitRes);
      setSettings({
        id: 'current',
        authorizedUsers: [],
        companies: companiesRes,
        departments: departmentsRes.departments || departmentsRes,
        currencies: ['AUD', 'USD', 'EUR', 'GBP'],
        frequencies: ['weekly', 'fortnightly', 'monthly', 'quarterly', 'annually', 'ad-hoc'],
        defaultSettings: {
          currency: 'AUD',
          frequency: 'monthly',
          approvalRequired: true
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      toast.error("Failed to load record details");
    } finally {
      setIsLoading(false);
    }
  };
const handleSubmit = async (payload: any, selectedFiles: File[]) => {
  if (!id) return;
  setIsSaving(true);
  try {
    await financeApi.updateDirectDebit(id, payload);

    if (selectedFiles.length > 0) {
      for (const file of selectedFiles) {
        const uploadFormData = new FormData();
        uploadFormData.append('image', file);
        uploadFormData.append('context', 'finance');

        try {
          const uploadResult: any = await api.post('/api/upload/s3', uploadFormData, { skipErrorToast: true });

          if (uploadResult.url) {
            await financeApi.addAttachment({
              directDebitId: id,
              fileName: file.name,
              url: uploadResult.url,
              fileKey: uploadResult.key || uploadResult.filename,
              fileSize: uploadResult.size,
              fileType: uploadResult.mimetype,
              category: 'other'
            });
          }
        } catch (uploadError) {
          console.error(`Error uploading ${file.name}:`, uploadError);
          toast.error(`Failed to upload ${file.name}`);
        }
      }
    }

    toast.success("Direct debit record updated successfully");
    navigate(`/finance/direct-debit/${id}`);
  } catch (error: any) {
    toast.error(error.message || "Failed to update direct debit record");
  } finally {
    setIsSaving(false);
  }
};

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-6 w-6 animate-spin text-blue-600 mr-2" />
        <span>Loading Record...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate(`/finance/direct-debit/${id}`)} className="h-9 w-9 mt-1">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Edit Direct Debit</h1>
            <p className="text-slate-500 mt-1">Modifying record for {debit?.description}</p>
          </div>
        </div>
      </div>
      
      {debit && settings && (
        <DirectDebitForm 
          initialData={debit}
          onSubmit={handleSubmit}
          onCancel={() => navigate(`/finance/direct-debit/${id}`)}
          isLoading={isSaving}
          settings={settings}
        />
      )}
    </div>
  );
};

export default DirectDebitEditPage;
